import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Branding, fetchBranding } from "@/services/branding";

const STORAGE_KEY = "admin-panel-branding-v2";

interface BrandingContextValue {
  branding: Branding | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  apply: (next: Branding) => void;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(
  undefined,
);

const applyToDom = (b: Branding) => {
  const root = document.documentElement;
  const isDark = root.classList.contains("dark");
  root.style.setProperty("--primary", b.primary);
  root.style.setProperty("--secondary", b.secondary);
  root.style.setProperty("--accent", b.accent);
  root.style.setProperty("--background", isDark ? b.bgDark : b.bgLight);
  root.style.setProperty("--foreground", isDark ? b.textDark : b.textLight);
  root.style.setProperty("--card", isDark ? b.cardDark : b.cardLight);
  root.style.setProperty(
    "--card-foreground",
    isDark ? b.textDark : b.textLight,
  );
  root.style.setProperty("--popover", isDark ? b.cardDark : b.cardLight);
  root.style.setProperty(
    "--popover-foreground",
    isDark ? b.textDark : b.textLight,
  );
  root.style.setProperty(
    "--sidebar-background",
    isDark ? b.sidebarDark : b.sidebarLight,
  );
  root.style.setProperty(
    "--sidebar-foreground",
    isDark ? b.textDark : b.textLight,
  );
  root.style.setProperty("--ring", b.primary);
  root.style.setProperty("--sidebar-primary", b.primary);

  if (b.faviconUrl) {
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link) link.href = b.faviconUrl;
  }
  document.title = b.appName;
};

const readCache = (): Branding | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Branding) : null;
  } catch {
    return null;
  }
};

const writeCache = (b: Branding) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
  } catch {
    /* quota / private mode — cache is optional */
  }
};

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const [branding, setBranding] = useState<Branding | null>(() => readCache());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (branding) applyToDom(branding);
  }, [branding]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const fresh = await fetchBranding();
      setBranding(fresh);
      writeCache(fresh);
      setError(null);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const apply = useCallback((next: Branding) => {
    setBranding(next);
    writeCache(next);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Re-apply colors when light/dark toggles (because bg/text values differ)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (branding) applyToDom(branding);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [branding]);

  return (
    <BrandingContext.Provider
      value={{ branding, loading, error, refresh, apply }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextValue => {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
  return ctx;
};
